import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import { useParams } from 'react-router-dom';
import useFetchSessionData from '../utils/useFetchSessionData';
import { updateQuestionStep } from '../redux/dataSlice';
import socket, { joinRoom, connectSocket } from '../socket';

const Moderator = () => {
  const { id: sessionId } = useParams();
  const fetchSessionData = useFetchSessionData();
  const dispatch = useDispatch();
  const loading = useSelector((state) => state.data.loading);
  const dataLoaded = useSelector((state) => state.data.dataLoaded);

  const sessionData = useSelector((state) => state.data.sessionData);
  const currentQuestionIndex = useSelector((state) => state.data.currentQuestionIndex);

  const [openDialog, setOpenDialog] = useState(false);
  const [openRestartDialog, setOpenRestartDialog] = useState(false);

  const handleRestartSession = () => {
    console.log('handlerestart');
    fetchSessionData('moderator', sessionData.sessionId);
  };

  useEffect(() => {
    // Fetch moderator data when component loads
    fetchSessionData('moderator', sessionId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (dataLoaded && sessionData.sessionId === undefined) return;
    connectSocket();

    joinRoom(`moderator|${sessionData.sessionId}`);

    socket.on('restarted', handleRestartSession);

    return () => {
      socket.off('restarted', handleRestartSession);
      socket.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataLoaded]);

  if (loading || !sessionData) return null;

  // Handle Next Step
  const handleNextStep = () => {
    const currentQuestion = sessionData.allQuestions[currentQuestionIndex];
    let currentStep = currentQuestion?.step;

    // Determine the maximum step value
    const maxStep = Math.max(...sessionData.questionStatus.map((status) => status.id));

    if (currentStep === 1) {
      // If current step is 1, move to the next question and reset step to max
      const newQuestionIndex = currentQuestionIndex + 1;

      if (newQuestionIndex < sessionData.allQuestions.length) {
        currentStep = maxStep;

        // Update the Redux store
        dispatch(
          updateQuestionStep({
            questionIndex: newQuestionIndex,
            step: currentStep,
            currentQuestionIndex: newQuestionIndex
          })
        );

        // Emit the socket event
        socket.emit('changeQuestion', {
          sessionId: sessionData.sessionId,
          questionIndex: newQuestionIndex,
          step: currentStep
        });
      } else {
        // No more questions left
        console.log('No more questions.');
      }
    } else {
      // Decrease the step by 1 OR if we have an override, override
      currentStep = currentQuestion.nextStep ? currentQuestion.nextStep : currentStep - 1;

      // Update the Redux store
      dispatch(
        updateQuestionStep({
          questionIndex: currentQuestionIndex,
          step: currentStep
        })
      );

      // Emit the socket event
      socket.emit('changeQuestion', {
        sessionId: sessionData.sessionId,
        questionIndex: currentQuestionIndex,
        step: currentStep
      });
    }
  };

  // Handle dialog open and close
  const handleDialogOpen = () => {
    setOpenDialog(true);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
  };

  // Handle Restart Dialog open and close
  const handleRestartDialogOpen = () => {
    setOpenRestartDialog(true);
  };

  const handleRestartDialogClose = () => {
    setOpenRestartDialog(false);
  };

  const handleRestart = () => {
    // Emit the socket event to the backend
    socket.emit('restartSession', {
      sessionId: sessionData.sessionId
    });

    // Close the restart dialog
    handleRestartDialogClose();
  };

  const handleCloseSession = () => {
    // Add logic to close the session here
    console.log('Session closed');

    socket.emit('changeSessionStatus', {
      sessionId: sessionData.sessionId,
      sessionStatus: 3
    });

    handleDialogClose();
  };

  const renderBody = () => {
    if (!sessionData.allQuestions) return null;
    const currentQuestion = sessionData.allQuestions[currentQuestionIndex];
    if (!currentQuestion) return null;

    const currentStep = currentQuestion.step;
    const currentStatus =
      sessionData.questionStatus.find((status) => status.id === currentStep)?.name || 'Unknown Status';

    // Determine if we're on the last question and last step
    const isLastQuestion = currentQuestionIndex === sessionData.allQuestions.length - 1;
    const isLastStep = currentStep === 1;

    return (
      <>
        <h2>Current Question: {currentQuestionIndex + 1}</h2>
        <h2>Current Status: {currentStatus}</h2>

        {/* Conditionally render the Next Step Button */}
        {!isLastQuestion || !isLastStep ? (
          <Button variant="contained" color="primary" onClick={handleNextStep}>
            Next Step
          </Button>
        ) : null}

        <div className="questions-container">
          {sessionData.allQuestions.map((question, index) => (
            <div key={index} className={`question-item ${currentQuestionIndex === index ? 'current-question' : ''}`}>
              <h3 dangerouslySetInnerHTML={{ __html: question.question }}></h3>
              <p>{question.options.join(', ')}</p>
            </div>
          ))}
        </div>
      </>
    );
  };

  return (
    <div className="moderator-container">
      {renderBody()}

      {/* Restart Session Button */}
      <Button variant="contained" color="secondary" onClick={handleRestartDialogOpen}>
        Restart Session
      </Button>

      {/* Close Session Button */}
      <Button variant="contained" color="error" onClick={handleDialogOpen}>
        Close Session
      </Button>

      {/* Restart Session Dialog */}
      <Dialog open={openRestartDialog} onClose={handleRestartDialogClose}>
        <DialogTitle>Restart Session</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to restart the session? This will reset to the first question and notify all
            attendees.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRestartDialogClose} color="primary">
            Cancel
          </Button>
          <Button onClick={handleRestart} color="secondary">
            Restart Session
          </Button>
        </DialogActions>
      </Dialog>

      {/* Close Session Dialog */}
      <Dialog open={openDialog} onClose={handleDialogClose}>
        <DialogTitle>Close Session</DialogTitle>
        <DialogContent>
          <DialogContentText>Are you sure you want to close the session?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} color="primary">
            Cancel
          </Button>
          <Button onClick={handleCloseSession} color="error">
            Close Session
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Moderator;
